import { createFileRoute, redirect } from "@tanstack/react-router";

import { authQueries } from "@/api/resources/auth/queries";
import { setInitialUserTimezone, setUserAnalytics, syncSSOUserAttributes } from "@/api/resources/auth/utils";
import { usersToWorkspacesQueries } from "@/api/resources/usersToWorkspaces/queries";
import { workspacesQueries } from "@/api/resources/workspaces/queries";
import { selectorDirectAccessWorkspaces } from "@/api/resources/workspaces/selectors";
import { getIdealWorkspace } from "@/api/resources/workspaces/utils";

export const Route = createFileRoute("/_auth/")({
  // get/load user details, redirect to
  // 1) onboarding if profile not filled out
  // get/load all workspaces, redirect to
  // 2) to onboarding if none available
  // 3) next-best workspace
  beforeLoad: async ({ context }) => {
    // * get current user claims
    const claims = await context.queryClient.fetchQuery(authQueries.getAuthClaims());

    if (!claims) {
      throw redirect({
        to: "/login",
      });
    }

    // * get current user profile needed for onboarding redirect
    const profile = await context.queryClient.ensureQueryData(authQueries.getAuthUserProfile(claims.sub));
    if (!profile) {
      throw redirect({
        to: "/login",
      });
    }

    // * sync SSO user attributes (they can change, even after signup)
    await syncSSOUserAttributes(claims);

    // * initialize tracking analytics for user
    setUserAnalytics(profile);

    // * check and save current user timezone (if deviating from UTC)
    await setInitialUserTimezone(profile);

    // * if the user has no profile details, redirect to onboarding
    if (!profile.first_name || !profile.last_name) {
      throw redirect({
        to: "/welcome",
      });
    }

    // * get current user permissions needed for workspace selection
    const permissions = await context.queryClient.ensureQueryData(authQueries.getAuthUserPermissions(claims.sub));
    const workspaces = await context.queryClient.ensureQueryData(workspacesQueries.listAll());
    const usersToWorkspaces = await context.queryClient.ensureQueryData(
      usersToWorkspacesQueries.listByUserId(claims.sub),
    );
    const directAccessWorkspaces = selectorDirectAccessWorkspaces(workspaces, permissions, usersToWorkspaces);

    // * if the user has no access to any workspaces, redirect to onboarding
    if (directAccessWorkspaces.length <= 0) {
      throw redirect({
        to: "/welcome",
      });
    } else {
      const nextWorkspace = getIdealWorkspace(directAccessWorkspaces);

      // * redirect to next workspace
      throw redirect({
        to: "/workspaces/$workspace_id",
        params: {
          workspace_id: nextWorkspace.id,
        },
      });
    }
  },
  component: () => null,
});
